import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";

// --- Types ---
type Rect = { x: number; y: number; width: number; height: number };
type ObstacleType = "TREE" | "ROCK" | "SNOWMAN";
type Obstacle = Rect & { id: number; passed: boolean; type: ObstacleType };
type Difficulty = "EASY" | "NORMAL" | "HARD";
type TrailPoint = { x: number; y: number; id: number };

// --- Constants & Settings ---
const PLAYER_WIDTH = 28;
const PLAYER_HEIGHT = 32;
const OBSTACLE_WIDTH = 40;
const OBSTACLE_HEIGHT = 50;

const DIFFICULTY_SETTINGS = {
  EASY: { initialSpeed: 4.0, speedIncrement: 0.2, spawnRateBase: 0.015 },
  NORMAL: { initialSpeed: 5.5, speedIncrement: 0.4, spawnRateBase: 0.035 },
  HARD: { initialSpeed: 7.5, speedIncrement: 0.7, spawnRateBase: 0.06 },
};

// --- SVG Icons ---
const IconMountain = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
  </svg>
);

const IconSnowflake = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="2" y1="12" x2="22" y2="12" />
    <line x1="12" y1="2" x2="12" y2="22" />
    <line x1="20" y1="16" x2="17" y2="12" />
    <line x1="17" y1="12" x2="20" y2="8" />
    <line x1="4" y1="8" x2="7" y2="12" />
    <line x1="7" y1="12" x2="4" y2="16" />
    <line x1="16" y1="4" x2="12" y2="7" />
    <line x1="12" y1="7" x2="8" y2="4" />
    <line x1="8" y1="20" x2="12" y2="17" />
    <line x1="12" y1="17" x2="16" y2="20" />
  </svg>
);

const IconLeaf = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </svg>
);

const IconStar = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const IconFlame = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  </svg>
);

const IconZap = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const IconRefresh = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

const IconHome = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const IconTrophy = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

// --- Audio Controller (Web Audio API) ---
class AudioController {
  private ctx: AudioContext | null = null;

  init() {
    if (!this.ctx) {
      this.ctx = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
    }
    if (this.ctx.state === "suspended") {
      this.ctx.resume();
    }
  }

  playTone(
    frequency: number,
    type: OscillatorType,
    duration: number,
    volume = 0.5,
  ) {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gainNode = this.ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(frequency, this.ctx.currentTime);

    gainNode.gain.setValueAtTime(volume, this.ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.001,
      this.ctx.currentTime + duration,
    );

    osc.connect(gainNode);
    gainNode.connect(this.ctx.destination);

    osc.start();
    osc.stop(this.ctx.currentTime + duration);
  }

  playCrash() {
    this.playTone(150, "sawtooth", 0.3, 0.6);
    setTimeout(() => this.playTone(80, "square", 0.5, 0.8), 50);
  }

  playScore() {
    this.playTone(800, "sine", 0.1, 0.15);
    setTimeout(() => this.playTone(1200, "sine", 0.15, 0.15), 100);
  }
}

const audio = new AudioController();

// --- Main Game Component ---
export default function App() {
  const [gameState, setGameState] = useState<"START" | "PLAYING" | "GAME_OVER">(
    "START",
  );
  const [score, setScore] = useState(0);
  const [isShaking, setIsShaking] = useState(false);

  const gameAreaRef = useRef<HTMLDivElement>(null);
  const getInitWidth = () =>
    typeof window !== "undefined" ? window.innerWidth : 400;
  const getInitHeight = () =>
    typeof window !== "undefined" ? window.innerHeight : 600;

  const [dimensions, setDimensions] = useState({
    width: getInitWidth(),
    height: getInitHeight(),
  });
  const dimRef = useRef({ width: getInitWidth(), height: getInitHeight() });

  useEffect(() => {
    if (!gameAreaRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setDimensions({ width, height });
          dimRef.current = { width, height };
        }
      }
    });
    observer.observe(gameAreaRef.current);
    return () => observer.disconnect();
  }, []);

  const playerRef = useRef<Rect>({
    x: getInitWidth() / 2 - PLAYER_WIDTH / 2,
    y: 120,
    width: PLAYER_WIDTH - 8,
    height: PLAYER_HEIGHT - 8,
  });

  const difficultyRef = useRef<Difficulty>("NORMAL");
  const velocityRef = useRef(0);
  const tiltRef = useRef(0);
  const bobRef = useRef(0);

  const obstaclesRef = useRef<Obstacle[]>([]);
  const trailRef = useRef<TrailPoint[]>([]);
  const trailIdCounter = useRef(0);
  const speedRef = useRef(DIFFICULTY_SETTINGS.NORMAL.initialSpeed);
  const keysRef = useRef<{ [key: string]: boolean }>({});
  const animationRef = useRef<number>(0);
  const obstacleIdCounter = useRef(0);

  const [, setTick] = useState(0);

  const [leftActive, setLeftActive] = useState(false);
  const [rightActive, setRightActive] = useState(false);

  const spawnObstacle = useCallback(() => {
    const x = Math.random() * (dimRef.current.width - OBSTACLE_WIDTH);
    const types: ObstacleType[] = ["TREE", "ROCK", "SNOWMAN"];
    const type =
      types[Math.floor(Math.random() * (types.length + 2))] || "TREE";

    let width = OBSTACLE_WIDTH - 10;
    let height = OBSTACLE_HEIGHT - 12;

    if (type === "ROCK") {
      width = 30;
      height = 24;
    } else if (type === "SNOWMAN") {
      width = 26;
      height = 45;
    }

    obstaclesRef.current.push({
      id: obstacleIdCounter.current++,
      x,
      y: dimRef.current.height + Math.max(OBSTACLE_HEIGHT, height) + 50,
      width,
      height,
      passed: false,
      type,
    });
  }, []);

  const checkCollision = (rect1: Rect, rect2: Rect) => {
    return (
      rect1.x < rect2.x + rect2.width &&
      rect1.x + rect1.width > rect2.x &&
      rect1.y < rect2.y + rect2.height &&
      rect1.y + rect1.height > rect2.y
    );
  };

  const startGame = (diff: Difficulty) => {
    audio.init();
    difficultyRef.current = diff;
    setGameState("PLAYING");
    setScore(0);
    setIsShaking(false);
    playerRef.current = {
      x: dimRef.current.width / 2 - PLAYER_WIDTH / 2,
      y: 120,
      width: PLAYER_WIDTH - 8,
      height: PLAYER_HEIGHT - 8,
    };
    velocityRef.current = 0;
    tiltRef.current = 0;
    bobRef.current = 0;
    obstaclesRef.current = [];
    trailRef.current = [];
    speedRef.current = DIFFICULTY_SETTINGS[diff].initialSpeed;
    obstacleIdCounter.current = 0;
    keysRef.current = {};
    setLeftActive(false);
    setRightActive(false);
  };

  const triggerShake = () => {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
  };

  const gameLoop = useCallback(() => {
    if (gameState !== "PLAYING") return;

    const settings = DIFFICULTY_SETTINGS[difficultyRef.current];

    if (keysRef.current["ArrowLeft"]) velocityRef.current -= 0.85;
    if (keysRef.current["ArrowRight"]) velocityRef.current += 0.85;

    velocityRef.current *= 0.88;
    playerRef.current.x += velocityRef.current;

    if (playerRef.current.x <= 0) {
      playerRef.current.x = 0;
      velocityRef.current = Math.abs(velocityRef.current) * 0.5;
    } else if (
      playerRef.current.x >=
      dimRef.current.width - playerRef.current.width
    ) {
      playerRef.current.x = dimRef.current.width - playerRef.current.width;
      velocityRef.current = -Math.abs(velocityRef.current) * 0.5;
    }

    const targetTilt = velocityRef.current * 4.5;
    tiltRef.current += (targetTilt - tiltRef.current) * 0.2;

    bobRef.current =
      Math.sin(Date.now() * 0.015) * (1 + speedRef.current * 0.2);

    // Update Ski Trail
    if (Math.abs(velocityRef.current) > 0.5 || Date.now() % 3 === 0) {
      trailRef.current.push({
        x: playerRef.current.x + PLAYER_WIDTH / 2,
        y: playerRef.current.y + PLAYER_HEIGHT - 10,
        id: trailIdCounter.current++,
      });
    }

    trailRef.current.forEach((pt) => {
      pt.y -= speedRef.current;
    });
    trailRef.current = trailRef.current.filter((pt) => pt.y > -50);

    let currentScore = score;
    let crashed = false;

    for (let i = obstaclesRef.current.length - 1; i >= 0; i--) {
      const obs = obstaclesRef.current[i];
      obs.y -= speedRef.current;

      if (checkCollision(playerRef.current, obs)) {
        crashed = true;
        break;
      }

      if (!obs.passed && obs.y < playerRef.current.y) {
        obs.passed = true;
        currentScore += 1;
        audio.playScore();
        if (currentScore % 5 === 0) speedRef.current += settings.speedIncrement;
      }

      if (obs.y + obs.height < -50) {
        obstaclesRef.current.splice(i, 1);
      }
    }

    if (crashed) {
      audio.playCrash();
      triggerShake();
      setGameState("GAME_OVER");
      return;
    }

    const spawnMultiplier = dimRef.current.width / 400;
    if (
      Math.random() <
      (settings.spawnRateBase + speedRef.current * 0.002) * spawnMultiplier
    ) {
      spawnObstacle();
    }

    if (currentScore !== score) setScore(currentScore);

    setTick((t) => t + 1);
    animationRef.current = requestAnimationFrame(gameLoop);
  }, [gameState, score, spawnObstacle]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      keysRef.current[e.key] = true;
      if (e.key === "ArrowLeft") setLeftActive(true);
      if (e.key === "ArrowRight") setRightActive(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      keysRef.current[e.key] = false;
      if (e.key === "ArrowLeft") setLeftActive(false);
      if (e.key === "ArrowRight") setRightActive(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  useEffect(() => {
    if (gameState === "PLAYING") {
      animationRef.current = requestAnimationFrame(gameLoop);
    }
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [gameState, gameLoop]);

  const snowSprayScale = Math.min(
    1,
    Math.max(0, (Math.abs(tiltRef.current) - 10) / 20),
  );
  const isLeaningLeft = tiltRef.current < 0;

  return (
    <div className={`app-container ${isShaking ? "shake" : ""}`}>
      <div className="game-area" ref={gameAreaRef}>
        <svg
          viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
          preserveAspectRatio="xMidYMid meet"
          width="100%"
          height="100%"
        >
          <defs>
            {/* Same Defs as Original */}
            <filter id="snowTexture" x="0" y="0" width="100%" height="100%">
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.015 0.02"
                numOctaves="4"
                stitchTiles="stitch"
                result="noise"
              />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0.85  0 0 0 0 0.9  0 0 0 0 0.95  0 0 0 0.35 0"
                in="noise"
                result="coloredNoise"
              />
              <feComposite
                operator="in"
                in="coloredNoise"
                in2="SourceGraphic"
                result="composite"
              />
              <feBlend mode="multiply" in="composite" in2="SourceGraphic" />
            </filter>
            <filter
              id="softShadow"
              x="-20%"
              y="-20%"
              width="140%"
              height="140%"
            >
              <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
            </filter>
            <linearGradient id="skySky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1e3a8a" />
              <stop offset="50%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#bfdbfe" />
            </linearGradient>
            <linearGradient id="mountainShade" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#94a3b8" />
              <stop offset="100%" stopColor="#475569" />
            </linearGradient>
            <linearGradient id="snowGround" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#e2e8f0" />
              <stop offset="15%" stopColor="#f8fafc" />
              <stop offset="100%" stopColor="#ffffff" />
            </linearGradient>
            <linearGradient id="trunkGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#451a03" />
              <stop offset="50%" stopColor="#78350f" />
              <stop offset="100%" stopColor="#451a03" />
            </linearGradient>
            <linearGradient id="treeDark" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#064e3b" />
              <stop offset="100%" stopColor="#022c22" />
            </linearGradient>
            <linearGradient id="treeLight" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#059669" />
              <stop offset="100%" stopColor="#064e3b" />
            </linearGradient>
            <linearGradient id="rockFace1" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#64748b" />
              <stop offset="100%" stopColor="#334155" />
            </linearGradient>
            <linearGradient id="rockFace2" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#475569" />
              <stop offset="100%" stopColor="#1e293b" />
            </linearGradient>
            <radialGradient id="snowSphere" cx="30%" cy="30%" r="70%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="70%" stopColor="#cbd5e1" />
              <stop offset="100%" stopColor="#94a3b8" />
            </radialGradient>
            <linearGradient id="suitGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="100%" stopColor="#991b1b" />
            </linearGradient>
            <linearGradient id="goggleReflect" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f59e0b" />
              <stop offset="50%" stopColor="#d97706" />
              <stop offset="100%" stopColor="#78350f" />
            </linearGradient>
          </defs>

          {/* Background Sky & Mountains */}
          <rect
            x="0"
            y="0"
            width={dimensions.width}
            height={150}
            fill="url(#skySky)"
          />
          <g
            transform={`translate(0, ${(gameState === "PLAYING" ? (Date.now() / 60) % 300 : 0) - 150})`}
          >
            <path
              d={`M 0,150 L ${dimensions.width * 0.15},60 L ${dimensions.width * 0.3},110 L ${dimensions.width * 0.55},40 L ${dimensions.width * 0.8},120 L ${dimensions.width},70 L ${dimensions.width},0 L 0,0 Z`}
              fill="#94a3b8"
              opacity="0.8"
            />
            <path
              d={`M 0,200 L ${dimensions.width * 0.25},100 L ${dimensions.width * 0.45},160 L ${dimensions.width * 0.75},80 L ${dimensions.width},150 L ${dimensions.width},0 L 0,0 Z`}
              fill="url(#mountainShade)"
            />
          </g>

          {/* Ground */}
          <rect
            x="0"
            y="0"
            width={dimensions.width}
            height={dimensions.height}
            fill="url(#snowGround)"
          />
          <rect
            x="0"
            y="0"
            width={dimensions.width}
            height={dimensions.height}
            fill="#ffffff"
            filter="url(#snowTexture)"
            opacity="0.9"
          />

          {/* Render Ski Trail (Behind Player) */}
          {trailRef.current.length > 1 && (
            <>
              <polyline
                points={trailRef.current.map((p) => `${p.x},${p.y}`).join(" ")}
                fill="none"
                stroke="#cbd5e1"
                strokeWidth="14"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.5"
              />
              <polyline
                points={trailRef.current.map((p) => `${p.x},${p.y}`).join(" ")}
                fill="none"
                stroke="#ffffff"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.8"
              />
            </>
          )}

          {/* Render Obstacles (Same as original but condensed for brevity) */}
          {obstaclesRef.current.map((obs) => (
            <g key={obs.id} transform={`translate(${obs.x}, ${obs.y})`}>
              <ellipse
                cx={obs.width / 2}
                cy={obs.height - 2}
                rx={obs.type === "ROCK" ? "20" : "18"}
                ry="8"
                fill="rgba(15, 23, 42, 0.25)"
                filter="url(#softShadow)"
              />
              {obs.type === "TREE" && (
                <>
                  <rect
                    x={OBSTACLE_WIDTH / 2 - 4}
                    y={OBSTACLE_HEIGHT - 10}
                    width="8"
                    height="14"
                    rx="2"
                    fill="url(#trunkGrad)"
                  />
                  <polygon
                    points={`0,38 ${OBSTACLE_WIDTH / 2},12 ${OBSTACLE_WIDTH},38`}
                    fill="url(#treeDark)"
                  />
                  <polygon
                    points={`12,28 ${OBSTACLE_WIDTH / 2},15 ${OBSTACLE_WIDTH - 6},36 ${OBSTACLE_WIDTH / 2 + 2},38`}
                    fill="#f8fafc"
                    opacity="0.9"
                  />
                  <polygon
                    points={`4,26 ${OBSTACLE_WIDTH / 2},4 ${OBSTACLE_WIDTH - 4},26`}
                    fill="url(#treeLight)"
                  />
                  <polygon
                    points={`10,18 ${OBSTACLE_WIDTH / 2},8 ${OBSTACLE_WIDTH - 8},24 ${OBSTACLE_WIDTH / 2 + 2},26`}
                    fill="#f8fafc"
                    opacity="0.95"
                  />
                  <polygon
                    points={`8,14 ${OBSTACLE_WIDTH / 2},-4 ${OBSTACLE_WIDTH - 8},14`}
                    fill="url(#treeLight)"
                  />
                  <polygon
                    points={`12,8 ${OBSTACLE_WIDTH / 2},-4 ${OBSTACLE_WIDTH / 2 + 5},12 ${OBSTACLE_WIDTH / 2},14`}
                    fill="#ffffff"
                    opacity="1"
                  />
                </>
              )}
              {obs.type === "ROCK" && (
                <>
                  <path
                    d={`M 0,${obs.height} L 8,${obs.height - 18} L 20,${obs.height - 24} L ${obs.width},${obs.height} Z`}
                    fill="url(#rockFace2)"
                  />
                  <path
                    d={`M 0,${obs.height} L 8,${obs.height - 18} L 20,${obs.height - 6} L 12,${obs.height} Z`}
                    fill="url(#rockFace1)"
                  />
                  <path
                    d={`M 8,${obs.height - 18} L 20,${obs.height - 24} L 28,${obs.height - 12} L 20,${obs.height - 6} Z`}
                    fill="#94a3b8"
                  />
                  <path
                    d={`M 6,${obs.height - 16} Q 12,${obs.height - 20} 20,${obs.height - 24} Q 26,${obs.height - 14} 20,${obs.height - 6} Q 14,${obs.height - 12} 6,${obs.height - 16}`}
                    fill="url(#snowSphere)"
                  />
                </>
              )}
              {obs.type === "SNOWMAN" && (
                <>
                  <path
                    d={`M ${obs.width / 2},${obs.height - 20} L -6,${obs.height - 30} L -10,${obs.height - 26}`}
                    stroke="#451a03"
                    strokeWidth="2"
                    fill="none"
                  />
                  <path
                    d={`M ${obs.width / 2},${obs.height - 20} L ${obs.width + 6},${obs.height - 28} L ${obs.width + 10},${obs.height - 32}`}
                    stroke="#451a03"
                    strokeWidth="2"
                    fill="none"
                  />
                  <circle
                    cx={obs.width / 2}
                    cy={obs.height}
                    r="14"
                    fill="url(#snowSphere)"
                  />
                  <circle
                    cx={obs.width / 2}
                    cy={obs.height - 18}
                    r="11"
                    fill="url(#snowSphere)"
                  />
                  <circle
                    cx={obs.width / 2}
                    cy={obs.height - 34}
                    r="8.5"
                    fill="url(#snowSphere)"
                  />
                  <ellipse
                    cx={obs.width / 2}
                    cy={obs.height - 26}
                    rx="8"
                    ry="3"
                    fill="rgba(0,0,0,0.2)"
                  />
                  <path
                    d={`M ${obs.width / 2 - 9},${obs.height - 26} Q ${obs.width / 2},${obs.height - 22} ${obs.width / 2 + 9},${obs.height - 26} L ${obs.width / 2 + 12},${obs.height - 12} L ${obs.width / 2 + 6},${obs.height - 14} Z`}
                    fill="#ef4444"
                  />
                  <circle
                    cx={obs.width / 2 - 3}
                    cy={obs.height - 36}
                    r="1.5"
                    fill="#0f172a"
                  />
                  <circle
                    cx={obs.width / 2 + 3}
                    cy={obs.height - 36}
                    r="1.5"
                    fill="#0f172a"
                  />
                  <polygon
                    points={`${obs.width / 2},${obs.height - 34} ${obs.width / 2 + 14},${obs.height - 31} ${obs.width / 2},${obs.height - 32}`}
                    fill="#ea580c"
                  />
                  <circle
                    cx={obs.width / 2}
                    cy={obs.height - 20}
                    r="1.5"
                    fill="#0f172a"
                  />
                  <circle
                    cx={obs.width / 2}
                    cy={obs.height - 12}
                    r="1.5"
                    fill="#0f172a"
                  />
                  <rect
                    x={obs.width / 2 - 9}
                    y={obs.height - 43}
                    width="18"
                    height="3"
                    fill="#1e293b"
                    rx="1"
                  />
                  <rect
                    x={obs.width / 2 - 6}
                    y={obs.height - 54}
                    width="12"
                    height="12"
                    fill="#0f172a"
                    rx="1"
                  />
                  <rect
                    x={obs.width / 2 - 6}
                    y={obs.height - 46}
                    width="12"
                    height="3"
                    fill="#dc2626"
                  />
                </>
              )}
            </g>
          ))}

          {/* Render Player (Same as original) */}
          <g
            transform={`translate(${playerRef.current.x}, ${playerRef.current.y + bobRef.current}) rotate(${tiltRef.current} ${PLAYER_WIDTH / 2} ${PLAYER_HEIGHT / 2})`}
          >
            <ellipse
              cx={PLAYER_WIDTH / 2 - tiltRef.current * 0.3}
              cy={PLAYER_HEIGHT + 4 - bobRef.current}
              rx="18"
              ry="6"
              fill="rgba(15, 23, 42, 0.25)"
              filter="url(#softShadow)"
            />
            {snowSprayScale > 0 && (
              <g
                opacity={0.8 * snowSprayScale}
                transform={`scale(${0.6 + snowSprayScale * 0.4})`}
              >
                <circle
                  cx={isLeaningLeft ? PLAYER_WIDTH + 8 : -8}
                  cy="36"
                  r="6"
                  fill="#ffffff"
                  filter="url(#softShadow)"
                />
                <circle
                  cx={isLeaningLeft ? PLAYER_WIDTH + 14 : -14}
                  cy="32"
                  r="8"
                  fill="#ffffff"
                  filter="url(#softShadow)"
                />
                <circle
                  cx={isLeaningLeft ? PLAYER_WIDTH + 18 : -18}
                  cy="42"
                  r="10"
                  fill="#f8fafc"
                  filter="url(#softShadow)"
                />
              </g>
            )}
            <g transform={`translate(0, ${tiltRef.current * -0.15})`}>
              <path
                d="M -4,-2 L -4,34 Q -4,40 -8,44"
                stroke="#e2e8f0"
                strokeWidth="5"
                strokeLinecap="round"
                fill="none"
              />
              <path
                d="M -4,-2 L -4,34 Q -4,40 -8,44"
                stroke="#94a3b8"
                strokeWidth="1.5"
                fill="none"
              />
              <rect x="-6" y="16" width="4" height="8" fill="#1e293b" rx="1" />
              <path
                d="M -8,12 L -12,28"
                stroke="#0f172a"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <ellipse cx="-12" cy="26" rx="4" ry="1.5" fill="#334155" />
              <circle cx="-12" cy="28" r="1.5" fill="#e2e8f0" />
            </g>
            <g transform={`translate(0, ${tiltRef.current * 0.15})`}>
              <path
                d={`M ${PLAYER_WIDTH + 4},-2 L ${PLAYER_WIDTH + 4},34 Q ${PLAYER_WIDTH + 4},40 ${PLAYER_WIDTH + 8},44`}
                stroke="#e2e8f0"
                strokeWidth="5"
                strokeLinecap="round"
                fill="none"
              />
              <path
                d={`M ${PLAYER_WIDTH + 4},-2 L ${PLAYER_WIDTH + 4},34 Q ${PLAYER_WIDTH + 4},40 ${PLAYER_WIDTH + 8},44`}
                stroke="#94a3b8"
                strokeWidth="1.5"
                fill="none"
              />
              <rect
                x={PLAYER_WIDTH + 2}
                y="16"
                width="4"
                height="8"
                fill="#1e293b"
                rx="1"
              />
              <path
                d={`M ${PLAYER_WIDTH + 8},12 L ${PLAYER_WIDTH + 12},28`}
                stroke="#0f172a"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <ellipse
                cx={PLAYER_WIDTH + 12}
                cy="26"
                rx="4"
                ry="1.5"
                fill="#334155"
              />
              <circle cx={PLAYER_WIDTH + 12} cy="28" r="1.5" fill="#e2e8f0" />
            </g>
            <rect
              x="2"
              y="6"
              width={PLAYER_WIDTH - 4}
              height={PLAYER_HEIGHT - 10}
              rx="8"
              fill="url(#suitGrad)"
            />
            <rect
              x="-1"
              y="8"
              width="6"
              height="14"
              rx="3"
              fill="url(#suitGrad)"
              transform="rotate(15 2 8)"
            />
            <rect
              x={PLAYER_WIDTH - 5}
              y="8"
              width="6"
              height="14"
              rx="3"
              fill="url(#suitGrad)"
              transform={`rotate(-15 ${PLAYER_WIDTH - 2} 8)`}
            />
            <circle cx={PLAYER_WIDTH / 2} cy="8" r="9" fill="url(#suitGrad)" />
            <path
              d={`M ${PLAYER_WIDTH / 2 - 6}, 4 A 7 7 0 0 1 ${PLAYER_WIDTH / 2 + 4}, 2`}
              stroke="rgba(255,255,255,0.4)"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
            <rect
              x="4"
              y="4"
              width={PLAYER_WIDTH - 8}
              height="8"
              rx="4"
              fill="url(#goggleReflect)"
            />
            <rect x="0" y="6" width="4" height="3" fill="#1e293b" />
            <rect
              x={PLAYER_WIDTH - 4}
              y="6"
              width="4"
              height="3"
              fill="#1e293b"
            />
            <path
              d={`M 6,6 Q ${PLAYER_WIDTH / 2},4 ${PLAYER_WIDTH - 6},6`}
              stroke="#ffffff"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
              opacity="0.8"
            />
            <path
              d={`M ${PLAYER_WIDTH / 2},16 Q ${PLAYER_WIDTH / 2 - velocityRef.current * 4},-2 ${PLAYER_WIDTH / 2 - velocityRef.current * 3},-12`}
              stroke="#facc15"
              strokeWidth="5"
              strokeLinecap="round"
              fill="none"
            />
          </g>
        </svg>

        <div className="snow-layer"></div>

        {/* Catchy Start Screen */}
        {gameState === "START" && (
          <div className="overlay-container">
            <div className="glass-panel animate-float">
              <h1
                className="title"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                <IconMountain size={36} /> Softy Skier{" "}
                <IconSnowflake size={36} />
              </h1>
              <p className="subtitle">Pick Your Pace!</p>
              <div className="button-group">
                <button
                  onClick={() => startGame("EASY")}
                  className="btn-jelly btn-easy"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <IconLeaf size={20} /> Easy
                </button>
                <button
                  onClick={() => startGame("NORMAL")}
                  className="btn-jelly btn-normal"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <IconStar size={20} /> Normal
                </button>
                <button
                  onClick={() => startGame("HARD")}
                  className="btn-jelly btn-hard"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <IconFlame size={20} /> Hard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Impactful Game Over Screen */}
        {gameState === "GAME_OVER" && (
          <div className="overlay-container red-tint">
            <div className="glass-panel pop-in">
              <h1
                className="title wipeout-text"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                <IconZap size={40} /> WIPEOUT! <IconZap size={40} />
              </h1>
              <p className="subtitle">Ouch! That looked like it hurt.</p>
              <div className="score-box">
                <span>Final Score</span>
                <h2>{score}</h2>
              </div>
              <div className="button-group">
                <button
                  onClick={() => startGame(difficultyRef.current)}
                  className="btn-jelly btn-normal"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <IconRefresh size={20} /> Try Again
                </button>
                <button
                  onClick={() => setGameState("START")}
                  className="btn-jelly btn-danger"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <IconHome size={20} /> Main Menu
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Floating In-Game UI */}
        {gameState === "PLAYING" && (
          <div
            key={score}
            className="score-badge pop-in"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <IconTrophy size={20} /> {score}
          </div>
        )}
      </div>

      {/* Chunky, Intuitive Mobile Controls */}
      {gameState === "PLAYING" && (
        <div className="controls-container">
          <button
            className={`control-btn ${leftActive ? "active" : ""}`}
            onPointerDown={() => {
              keysRef.current["ArrowLeft"] = true;
              setLeftActive(true);
            }}
            onPointerUp={() => {
              keysRef.current["ArrowLeft"] = false;
              setLeftActive(false);
            }}
            onPointerLeave={() => {
              keysRef.current["ArrowLeft"] = false;
              setLeftActive(false);
            }}
            onContextMenu={(e) => e.preventDefault()}
          >
            <span className="arrow">◀</span> LEFT
          </button>
          <button
            className={`control-btn right ${rightActive ? "active" : ""}`}
            onPointerDown={() => {
              keysRef.current["ArrowRight"] = true;
              setRightActive(true);
            }}
            onPointerUp={() => {
              keysRef.current["ArrowRight"] = false;
              setRightActive(false);
            }}
            onPointerLeave={() => {
              keysRef.current["ArrowRight"] = false;
              setRightActive(false);
            }}
            onContextMenu={(e) => e.preventDefault()}
          >
            RIGHT <span className="arrow">▶</span>
          </button>
        </div>
      )}
    </div>
  );
}
